/**
 * Вкладка «Сроки годности»: раскладка блоков и ширина полосок. Чистые функции —
 * их проверяет `lib/__tests__/warehouse-shelf-life.test.ts` без рендера.
 */
import type {
  DirectionShelfLife,
  HiddenShelfLife,
  OrganizationShelfLife,
  ShelfLifeBucket,
} from "@/api/warehouse_dashboard";
import { ruNum } from "@/lib/format";

/** Блок вкладки — ОДНА пара «юрлицо × склад направления». */
export interface ShelfLifeBlock {
  org: OrganizationShelfLife;
  direction: DirectionShelfLife;
}

/**
 * Блоки в порядке ответа: юрлица — как пришли, внутри — склады как пришли
 * (бэкенд отдаёт МП, затем Опт). Фронт только разворачивает дерево в список,
 * не сортирует.
 */
export function shelfLifeBlocks(organizations: OrganizationShelfLife[]): ShelfLifeBlock[] {
  return organizations.flatMap((org) => org.directions.map((direction) => ({ org, direction })));
}

/**
 * Ширина полоски корзины, % — ОТНОСИТЕЛЬНО самой большой корзины склада:
 * она занимает всю ширину, остальные — долю от неё (так мелкие корзины
 * видны, а не сливаются в точку рядом с одной большой). Ноль — `0`, полоски
 * нет. Процент рядом с числом — другая величина (доля от «Всего»), его эта
 * функция не трогает.
 */
export function bucketBarWidths(
  direction: DirectionShelfLife,
  buckets: ShelfLifeBucket[],
): Record<string, number> {
  const qty = (key: string) => Math.max(direction.buckets[key] ?? 0, 0);
  const max = Math.max(0, ...buckets.map((b) => qty(b.key)));
  return Object.fromEntries(
    buckets.map((b) => [b.key, max > 0 ? (qty(b.key) / max) * 100 : 0]),
  );
}

/** «Скрыто настройкой: Беляева А. А. (1 234 шт); ООО Ромашка (5 шт)» или `null`. */
export function hiddenLine(hidden: HiddenShelfLife[]): string | null {
  if (hidden.length === 0) return null;
  return `Скрыто настройкой: ${hidden
    .map((h) => `${h.name} (${ruNum.format(h.total)} шт)`)
    .join("; ")}`;
}
